<script setup>
import DeleteConfirmationModal from "@/components/modal/DeleteConfirmationModal.vue";
import NotesModal from "@/components/note/NotesModal.vue";
import OpportunityProductsCard from "@/components/opportunity/OpportunityProductsCard.vue";
import AddProductModal from "@/components/product/AddProductModal.vue";
import EditProductModal from "@/components/product/EditProductModal.vue";
import { Button, DateSelector, Icon, Input, Modal, PopoverMenu, Select } from "@/components/ui";
import { OPPORTUNITY_OVERVIEW_ROUTE } from "@/router/routes";
import { useContact } from "@/composables/contacts/useContact";
import { useUsers } from "@/composables/organization/useUsers";
import { useOpportunityStages } from "@/composables/opportunities";
import { useRegisterOpportunity } from "@/composables/opportunities/useRegisterOpportunity";
import { useLeadSources } from "@/composables/lead-source/useLeadSources";
import { useFormDraft } from "@/composables/shared";
import { OPPORTUNITY_CONFIDENCE_VALUES } from "@/utils/const";
import { DealTypes, TaskType } from "@/utils/enums";
import { ErrorMessage, Field, Form, defineRule } from "vee-validate";
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

defineRule("required", (value, [fieldName = "Field"]) => {
  if (value === null || value === undefined || value === "") {
    return `${fieldName} is required`;
  }
  if (typeof value === "string" && value.length === 0) {
    return `${fieldName} is required`;
  }
  if (typeof value === "object" && Object.keys(value).length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});

// Props
const props = defineProps({
  contactId: {
    type: String,
    default: null,
  },
  initialDealName: {
    type: String,
    default: null,
  },
  initialLeadSourceId: {
    type: String,
    default: null,
  },
  showProducts: {
    type: Boolean,
    default: false,
  },
  showNotes: {
    type: Boolean,
    default: false,
  },
  showHeaderCard: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits(["close-modal", "opportunity-created"]);

// Get global properties
const instance = getCurrentInstance();
const $toast = useToast();
const $filters = instance?.proxy?.$filters;

// Router and store
const router = useRouter();
const store = useStore();
const account = computed(() => store.getters.account.data);
const organizationId = computed(() => account.value?.organizationId);

// Reactive state
const showAddProductModal = ref(false);
const showEditProductModal = ref(false);
const selectedProduct = ref(null);
const selectedProductIndex = ref(null);
const showNotesModal = ref(false);
const isEditingDeal = ref(false);
const originalDealName = ref(null);
const selectedLeadSource = ref(null);
const isSubmitting = ref(false);

const registerOpportunityObj = ref({
  name: null,
  confidence: null,
  expectedCloseDate: null,
  dealType: null,
  leadSourceId: null,
  opportunityStageId: null,
  contactId: null,
  contactIds: [],
  ownerId: null,
  organizationId: null,
  products: [],
});

// Draft persistence — scoped to the contact when opened from a contact's page (most
// common entry point), otherwise per-org for the standalone Opportunities list flow.
const { loadDraft: loadOpportunityDraft, clearDraft: clearOpportunityDraft } = useFormDraft(
  registerOpportunityObj,
  {
    key: `create-opportunity:${props.contactId ?? organizationId.value ?? "unknown"}`,
    serialize: (v) => ({
      name: v.name,
      confidence: v.confidence,
      expectedCloseDate: v.expectedCloseDate,
      dealType: v.dealType,
      leadSourceId: v.leadSourceId,
      opportunityStageId: v.opportunityStageId,
      ownerId: v.ownerId,
      products: v.products,
    }),
    isEmpty: (v) =>
      !v.name &&
      !v.confidence &&
      !v.expectedCloseDate &&
      !v.dealType &&
      !v.leadSourceId &&
      !v.opportunityStageId &&
      !v.ownerId &&
      !v.products?.length,
  }
);

// Contact query (only if contactId is provided)
const contactParams = computed(() => {
  if (!props.contactId || !organizationId.value) return null;
  return {
    id: props.contactId,
    organizationId: organizationId.value,
  };
});
const { data: contactData, isLoading: contactIsLoading } = useContact(contactParams);

// Opportunity stages query
const opportunityStagesParams = computed(() => ({
  organizationId: organizationId.value,
}));
const { data: opportunityStagesData, isLoading: opportunityStagesIsLoading } =
  useOpportunityStages(opportunityStagesParams);

// Users query
const usersParams = computed(() => ({
  organizationId: organizationId.value,
}));
const { data: usersData, isLoading: usersIsLoading } = useUsers(usersParams);

// Lead sources query
const { data: leadSourcesData } = useLeadSources();

// Register opportunity mutation
const { mutateAsync: registerOpportunityMutation, isPending: isRegistering } =
  useRegisterOpportunity();

// Computed properties
const contact = computed(() => {
  const data = contactData.value;
  return Array.isArray(data) ? data[0] : data;
});
const opportunityStages = computed(() => opportunityStagesData.value || []);
const users = computed(() => usersData.value || []);
const leadSources = computed(() => leadSourcesData.value || []);

const opportunityStagesFormatted = computed(() => {
  return opportunityStages.value.map((stage) => ({
    value: stage.id,
    label: stage.name,
  }));
});

const usersFormatted = computed(() => {
  return users.value.map((user) => ({
    value: user.id,
    label: $filters?.capitalize(user.fullName),
  }));
});

const confidenceOptions = computed(() => {
  return (OPPORTUNITY_CONFIDENCE_VALUES || []).map((confidence) => ({
    value: confidence,
    label: `${confidence} %`,
  }));
});

const contactOptions = computed(() => {
  if (!contact.value) return [];
  return [
    {
      value: contact.value.id,
      label: `${$filters?.capitalize(contact.value.fname)} ${$filters?.capitalize(contact.value.lname)}`,
    },
  ];
});

const dealTypeOptions = computed(() => {
  return Object.values(DealTypes).map((dealType) => ({
    value: dealType,
    label: dealType,
  }));
});

const leadSourceOptions = computed(() => {
  return leadSources.value.map((ls) => ({
    id: ls.id,
    name: ls.name,
    label: ls.name,
    value: ls.id,
  }));
});

const popoverMenuItems = computed(() => {
  if (!props.showHeaderCard) return [];
  return [
    {
      label: "Edit Deal Name",
      icon: "mdi:pencil-outline",
      onClick: () => {
        originalDealName.value = registerOpportunityObj.value.name;
        isEditingDeal.value = true;
      },
    },
  ];
});

// Methods
const setValues = () => {
  if (account.value) {
    registerOpportunityObj.value.ownerId = account.value.id;
    registerOpportunityObj.value.organizationId = account.value.organizationId;
  }
  if (props.contactId) {
    registerOpportunityObj.value.contactId = props.contactId;
    registerOpportunityObj.value.contactIds = [props.contactId];
  }
  if (props.initialLeadSourceId) {
    registerOpportunityObj.value.leadSourceId = props.initialLeadSourceId;
  }
  if (props.initialDealName) {
    registerOpportunityObj.value.name = props.initialDealName;
  }
};

const restoreOpportunityDraft = () => {
  const draft = loadOpportunityDraft();
  if (!draft) return;
  // Don't clobber values deliberately seeded via props (e.g. from MeetingDetailsModal)
  // with a stale draft saved under the same contact/org key.
  if (draft.name && !props.initialDealName) registerOpportunityObj.value.name = draft.name;
  if (draft.confidence) registerOpportunityObj.value.confidence = draft.confidence;
  if (draft.expectedCloseDate)
    registerOpportunityObj.value.expectedCloseDate = draft.expectedCloseDate;
  if (draft.dealType) registerOpportunityObj.value.dealType = draft.dealType;
  if (draft.leadSourceId && !props.initialLeadSourceId)
    registerOpportunityObj.value.leadSourceId = draft.leadSourceId;
  if (draft.opportunityStageId)
    registerOpportunityObj.value.opportunityStageId = draft.opportunityStageId;
  if (draft.ownerId) registerOpportunityObj.value.ownerId = draft.ownerId;
  if (draft.products?.length) registerOpportunityObj.value.products = draft.products;
};

const syncSelectedLeadSource = () => {
  if (registerOpportunityObj.value.leadSourceId && leadSourceOptions.value.length > 0) {
    const leadSource = leadSourceOptions.value.find(
      (ls) => ls.id === registerOpportunityObj.value.leadSourceId
    );
    if (leadSource) {
      selectedLeadSource.value = leadSource;
    }
  }
};

const removeProduct = (index) => {
  registerOpportunityObj.value.products.splice(index, 1);
};

const handleCancelEdit = () => {
  if (originalDealName.value !== null) {
    registerOpportunityObj.value.name = originalDealName.value;
  }
  isEditingDeal.value = false;
  originalDealName.value = null;
};

const handleSaveEdit = () => {
  isEditingDeal.value = false;
  originalDealName.value = null;
};

const handleAddProduct = (product) => {
  registerOpportunityObj.value.products.push(product);
};

const editProduct = (product, index) => {
  selectedProduct.value = { ...product };
  selectedProductIndex.value = index;
  showEditProductModal.value = true;
};

const handleProductUpdated = (updatedProduct) => {
  if (selectedProductIndex.value !== null) {
    registerOpportunityObj.value.products[selectedProductIndex.value] = updatedProduct;
    selectedProductIndex.value = null;
  }
  selectedProduct.value = null;
};

const onLeadSourceChange = (leadSource) => {
  if (leadSource) {
    selectedLeadSource.value = leadSource;
    registerOpportunityObj.value.leadSourceId = leadSource.id;
  } else {
    selectedLeadSource.value = null;
    registerOpportunityObj.value.leadSourceId = null;
  }
};

const registerOpportunity = async () => {
  isSubmitting.value = true;
  try {
    const res = await registerOpportunityMutation(registerOpportunityObj.value);
    if (res) {
      clearOpportunityDraft();
      $toast?.success("Opportunity created successfully");
      emit("opportunity-created", res);

      // Navigate to opportunity overview if standalone (no contactId)
      if (!props.contactId) {
        router.push({
          name: OPPORTUNITY_OVERVIEW_ROUTE,
          params: { id: res.id },
        });
      }

      closeModal();
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.msg ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to register opportunity. Please try again.";
    $toast?.error(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

const closeModal = () => {
  emit("close-modal");
};

// Watch contact data to set opportunity name
watch(
  contact,
  (newContact) => {
    if (
      newContact &&
      !registerOpportunityObj.value.name &&
      props.showHeaderCard &&
      !props.initialDealName
    ) {
      if (newContact.company) {
        registerOpportunityObj.value.name = `${newContact.company.name} - New deal`;
      } else {
        registerOpportunityObj.value.name = `${$filters?.capitalize(newContact.fname)} ${$filters?.capitalize(newContact.lname)} - New deal`;
      }
    }
  },
  { immediate: true }
);

// Watch lead source options to sync selected lead source
watch(
  leadSourceOptions,
  () => {
    syncSelectedLeadSource();
  },
  { immediate: true }
);

// Initialize
onMounted(() => {
  setValues();
  restoreOpportunityDraft();
});
</script>

<template>
  <Modal
    :show="true"
    headerVariant="blue"
    size="lg"
    :zIndex="10080"
    :closeOnBackdrop="false"
    :blurBackdrop="false"
    :backdropOpacity="0.5"
    :show-close-button="false"
    @close="closeModal"
  >
    <template #default>
      <NotesModal
        v-if="showNotes && showNotesModal && !!contact"
        :selectedContact="contact"
        :start-in-add-mode="true"
        @close-notes-modal="showNotesModal = false"
      />
      <AddProductModal
        v-if="showProducts && showAddProductModal"
        @add-product="handleAddProduct"
        @close-add-product-modal="showAddProductModal = false"
      />
      <EditProductModal
        v-if="showProducts && showEditProductModal"
        :selectedProduct="selectedProduct"
        @product-updated="handleProductUpdated"
        @close-edit-product-modal="showEditProductModal = false"
      />
    </template>
    <template #header>
      <div class="opp-header__text">
        <h2 class="opp-header__title">Create Opportunity</h2>
        <p class="opp-header__subtitle">Set up a new opportunity</p>
      </div>
      <button type="button" class="opp-header__close" @click="closeModal" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>
    <template #body>
      <Form id="create-opportunity-form" class="form" @submit="registerOpportunity">
        <!-- Header Card (if showHeaderCard is true) -->
        <div v-if="showHeaderCard" class="row">
          <div class="col-12">
            <div class="opportunity-header-card">
              <div class="opportunity-header-content">
                <div class="opportunity-header-left">
                  <div class="opportunity-avatar">
                    <div class="opportunity-avatar-icon">
                      <Icon icon="mdi:briefcase-outline" />
                    </div>
                  </div>
                  <div v-if="isEditingDeal" class="opportunity-name-edit">
                    <input
                      v-model="registerOpportunityObj.name"
                      placeholder="Enter deal name"
                      class="opportunity-name-inline-input"
                      type="text"
                      @keyup.enter="handleSaveEdit"
                      @keyup.escape="handleCancelEdit"
                    />
                  </div>
                  <div v-else class="opportunity-name-display">
                    <div v-if="contactIsLoading" class="opportunity-name-skeleton">
                      <div class="skeleton-bar skeleton-bar--long"></div>
                    </div>
                    <h6 v-else-if="registerOpportunityObj.name" class="opportunity-name">
                      {{ registerOpportunityObj.name }}
                    </h6>
                    <h6 v-else class="opportunity-name opportunity-name-placeholder">
                      Opportunity
                    </h6>
                  </div>
                </div>
                <PopoverMenu
                  v-if="popoverMenuItems.length > 0"
                  :items="popoverMenuItems"
                  placement="bottom"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="row" :class="{ 'form-fields-row': showHeaderCard }">
          <div class="col-12">
            <div v-if="!showHeaderCard" class="row">
              <div class="col-6">
                <div class="form-group">
                  <Field
                    v-slot="{ field }"
                    name="name"
                    :rules="{
                      required: ['Deal Name'],
                    }"
                    :model-value="registerOpportunityObj.name"
                    @update:model-value="registerOpportunityObj.name = $event"
                  >
                    <Input
                      :model-value="field.value"
                      @update:model-value="field.onChange($event)"
                      @blur="field.onBlur"
                      label="Deal Name"
                      type="text"
                      :required="true"
                      placeholder="Enter deal name"
                    />
                  </Field>
                  <ErrorMessage name="name" class="form-text text-danger error-message" />
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <Field
                    name="opportunityStageId"
                    :model-value="registerOpportunityObj.opportunityStageId"
                    @update:model-value="registerOpportunityObj.opportunityStageId = $event"
                    v-slot="{ field }"
                  >
                    <Select
                      :model-value="field.value"
                      @update:model-value="field.onChange($event)"
                      @blur="field.onBlur"
                      :options="opportunityStagesFormatted"
                      label="Opportunity Status"
                      placeholder="Select opportunity status"
                      :loading="opportunityStagesIsLoading"
                      :disabled="opportunityStagesIsLoading"
                    />
                  </Field>
                  <ErrorMessage
                    name="opportunityStageId"
                    class="form-text text-danger error-message"
                  />
                </div>
              </div>
            </div>

            <div v-else class="row">
              <div class="col-6">
                <div class="form-group">
                  <Field
                    name="opportunityStageId"
                    :rules="{
                      required: ['Opportunity Status'],
                    }"
                    :model-value="registerOpportunityObj.opportunityStageId"
                    @update:model-value="registerOpportunityObj.opportunityStageId = $event"
                    v-slot="{ field }"
                  >
                    <Select
                      :model-value="field.value"
                      @update:model-value="field.onChange($event)"
                      @blur="field.onBlur"
                      :options="opportunityStagesFormatted"
                      label="Opportunity Status"
                      :required="true"
                      placeholder="Select opportunity status"
                      :loading="opportunityStagesIsLoading"
                      :disabled="opportunityStagesIsLoading"
                    />
                  </Field>
                  <ErrorMessage
                    name="opportunityStageId"
                    class="form-text text-danger error-message"
                  />
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <Field
                    name="expectedCloseDate"
                    :rules="{
                      required: ['Expected Close Date'],
                    }"
                    :model-value="registerOpportunityObj.expectedCloseDate"
                    @update:model-value="registerOpportunityObj.expectedCloseDate = $event"
                    v-slot="{ field }"
                  >
                    <label for="expectedCloseDate" class="date-selector-label"
                      >Expected Close Date<span class="required-field">*</span></label
                    >
                    <DateSelector
                      :model-value="field.value"
                      @update:model-value="field.onChange($event)"
                      @blur="field.onBlur"
                    />
                  </Field>
                  <ErrorMessage
                    name="expectedCloseDate"
                    class="form-text text-danger error-message"
                  />
                </div>
              </div>
            </div>

            <div v-if="!showHeaderCard" class="row row-spacing">
              <div class="col-6">
                <div class="form-group">
                  <Field
                    name="confidence"
                    :model-value="registerOpportunityObj.confidence"
                    @update:model-value="registerOpportunityObj.confidence = $event"
                    v-slot="{ field }"
                  >
                    <Select
                      :model-value="field.value"
                      @update:model-value="field.onChange($event)"
                      @blur="field.onBlur"
                      :options="confidenceOptions"
                      label="Confidence"
                      placeholder="Select confidence"
                    />
                  </Field>
                  <ErrorMessage name="confidence" class="form-text text-danger error-message" />
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <Field
                    name="expectedCloseDate"
                    :model-value="registerOpportunityObj.expectedCloseDate"
                    @update:model-value="registerOpportunityObj.expectedCloseDate = $event"
                    v-slot="{ field }"
                  >
                    <DateSelector
                      :model-value="field.value"
                      @update:model-value="field.onChange($event)"
                      @blur="field.onBlur"
                      :disable-past="false"
                      label="Expected Close"
                    />
                  </Field>
                  <ErrorMessage
                    name="expectedCloseDate"
                    class="form-text text-danger error-message"
                  />
                </div>
              </div>
            </div>

            <div v-if="showHeaderCard" class="row" style="margin-top: 20px">
              <div class="col-6">
                <div class="form-group">
                  <Field
                    name="ownerId"
                    :rules="{
                      required: ['Owner'],
                    }"
                    :model-value="registerOpportunityObj.ownerId"
                    @update:model-value="registerOpportunityObj.ownerId = $event"
                    v-slot="{ field }"
                  >
                    <Select
                      :model-value="field.value"
                      @update:model-value="field.onChange($event)"
                      @blur="field.onBlur"
                      :options="usersFormatted"
                      label="Owner"
                      :required="true"
                      placeholder="Select owner"
                      :loading="usersIsLoading"
                      :disabled="usersIsLoading"
                    />
                  </Field>
                  <ErrorMessage name="ownerId" class="form-text text-danger error-message" />
                </div>
              </div>
              <div v-if="contactId" class="col-6">
                <div class="form-group">
                  <Field
                    name="contactId"
                    :rules="{
                      required: ['Contact'],
                    }"
                    :model-value="registerOpportunityObj.contactId"
                    @update:model-value="registerOpportunityObj.contactId = $event"
                    v-slot="{ field }"
                  >
                    <Select
                      :model-value="field.value"
                      @update:model-value="field.onChange($event)"
                      @blur="field.onBlur"
                      :options="contactOptions"
                      label="Contact"
                      :required="true"
                      :disabled="true"
                      placeholder="Contact"
                    />
                  </Field>
                  <ErrorMessage name="contactId" class="form-text text-danger error-message" />
                </div>
              </div>
            </div>

            <div v-else class="row" style="margin-top: 20px">
              <div class="col-12">
                <div class="form-group">
                  <Field
                    name="ownerId"
                    :model-value="registerOpportunityObj.ownerId"
                    @update:model-value="registerOpportunityObj.ownerId = $event"
                    v-slot="{ field }"
                  >
                    <Select
                      :model-value="field.value"
                      @update:model-value="field.onChange($event)"
                      @blur="field.onBlur"
                      :options="usersFormatted"
                      label="Owner"
                      placeholder="Select owner"
                      :loading="usersIsLoading"
                      :disabled="usersIsLoading"
                    />
                  </Field>
                  <ErrorMessage name="ownerId" class="form-text text-danger error-message" />
                </div>
              </div>
            </div>

            <div v-if="showHeaderCard" class="row" style="margin-top: 20px">
              <div class="col-6">
                <div class="form-group">
                  <Field
                    name="dealType"
                    :rules="{
                      required: ['Deal Type'],
                    }"
                    :model-value="registerOpportunityObj.dealType"
                    @update:model-value="registerOpportunityObj.dealType = $event"
                    v-slot="{ field }"
                  >
                    <Select
                      :model-value="field.value"
                      @update:model-value="field.onChange($event)"
                      @blur="field.onBlur"
                      :options="dealTypeOptions"
                      label="Deal Type"
                      :required="true"
                      placeholder="Select deal type"
                    />
                  </Field>
                  <ErrorMessage name="dealType" class="form-text text-danger error-message" />
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <Field
                    name="confidence"
                    :rules="{
                      required: ['Confidence'],
                    }"
                    :model-value="registerOpportunityObj.confidence"
                    @update:model-value="registerOpportunityObj.confidence = $event"
                    v-slot="{ field }"
                  >
                    <Select
                      :model-value="field.value"
                      @update:model-value="field.onChange($event)"
                      @blur="field.onBlur"
                      :options="confidenceOptions"
                      label="Confidence"
                      :required="true"
                      placeholder="Select confidence"
                    />
                  </Field>
                  <ErrorMessage name="confidence" class="form-text text-danger error-message" />
                </div>
              </div>
            </div>

            <div v-else class="row" style="margin-top: 20px">
              <div class="col-6">
                <div class="form-group">
                  <Field
                    name="dealType"
                    :model-value="registerOpportunityObj.dealType"
                    @update:model-value="registerOpportunityObj.dealType = $event"
                    v-slot="{ field }"
                  >
                    <Select
                      :model-value="field.value"
                      @update:model-value="field.onChange($event)"
                      @blur="field.onBlur"
                      :options="dealTypeOptions"
                      label="Deal Type"
                      placeholder="Select deal type"
                    />
                  </Field>
                  <ErrorMessage name="dealType" class="form-text text-danger error-message" />
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <Field
                    name="leadSource"
                    :rules="{
                      required: ['Lead Source'],
                    }"
                    v-model="selectedLeadSource"
                    v-slot="{ field }"
                  >
                    <Select
                      :model-value="field.value"
                      @update:model-value="
                        field.onChange($event);
                        onLeadSourceChange($event);
                      "
                      @blur="field.onBlur"
                      @focus="field.onFocus"
                      :options="leadSourceOptions"
                      label="Lead Source"
                      :required="true"
                      placeholder="Select lead source"
                      label-key="name"
                      value-key="id"
                      :return-object="true"
                    />
                  </Field>
                  <ErrorMessage name="leadSource" class="form-text text-danger error-message" />
                </div>
              </div>
            </div>

            <div v-if="showHeaderCard" class="row" style="margin-top: 20px">
              <div class="col-12">
                <div class="form-group">
                  <Field
                    name="leadSource"
                    :rules="{
                      required: ['Lead Source'],
                    }"
                    v-model="selectedLeadSource"
                    v-slot="{ field }"
                  >
                    <Select
                      :model-value="field.value"
                      @update:model-value="
                        field.onChange($event);
                        onLeadSourceChange($event);
                      "
                      @blur="field.onBlur"
                      @focus="field.onFocus"
                      :options="leadSourceOptions"
                      label="Lead Source"
                      :required="true"
                      placeholder="Select lead source"
                      label-key="name"
                      value-key="id"
                      :return-object="true"
                    />
                  </Field>
                  <ErrorMessage name="leadSource" class="form-text text-danger error-message" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="showProducts || showNotes"
          class="row justify-content-between action-buttons-row"
          style="margin-bottom: 5px"
        >
          <div v-if="showNotes" class="col-auto">
            <button type="button" class="btn btn-new-field" @click="showNotesModal = true">
              <Icon icon="mdi:plus" class="btn-new-field-icon" />
              <span class="btn-new-field-text">Add Note</span>
            </button>
          </div>
          <div v-if="showProducts" class="col-auto">
            <button type="button" class="btn btn-new-field" @click="showAddProductModal = true">
              <Icon icon="mdi:plus" class="btn-new-field-icon" />
              <span class="btn-new-field-text">Add Product</span>
            </button>
          </div>
        </div>
        <OpportunityProductsCard
          v-if="showProducts"
          :products="registerOpportunityObj.products"
          @remove-product="removeProduct"
          @edit-product="editProduct"
        />
      </Form>
    </template>
    <template #footer>
      <div class="modal-footer-buttons">
        <Button
          type="button"
          variant="secondary"
          size="md"
          @click="closeModal()"
          :disabled="isSubmitting || isRegistering"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="create-opportunity-form"
          variant="primary"
          size="md"
          :loading="isSubmitting || isRegistering"
          :disabled="isSubmitting || isRegistering"
        >
          Create Opportunity
        </Button>
      </div>
    </template>
  </Modal>
</template>

<style scoped>
.opp-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.opp-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
}
.opp-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}
.opp-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}
.opp-header__close:hover {
  background: rgba(255, 255, 255, 0.35);
}

.opportunity-header-card {
  background-color: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 16px;
}

.opportunity-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.opportunity-header-content :deep(.popover-menu__trigger) {
  width: 24px;
  height: 24px;
}

.opportunity-header-content :deep(.popover-menu__trigger .iconify) {
  font-size: 18px;
  width: 18px;
  height: 18px;
}

.opportunity-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.opportunity-avatar {
  flex-shrink: 0;
}

.opportunity-avatar-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3e3efe 0%, #2d60ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 20px;
  flex-shrink: 0;
}

.opportunity-name-display {
  display: flex;
  align-items: center;
}

.opportunity-name {
  font-weight: 600;
  font-size: 13px;
  color: #101828;
  margin: 0;
  line-height: 1.3;
}

.opportunity-name-placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.opportunity-name-skeleton {
  display: flex;
  align-items: center;
  height: 20px;
  flex: 1;
}

.skeleton-bar {
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}

.skeleton-bar--long {
  width: 180px;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.opportunity-name-edit {
  flex: 1;
}

.opportunity-name-inline-input {
  font-weight: 600;
  font-size: 14px;
  color: #101828;
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  padding: 6px 0;
  margin: 0;
  line-height: 1.4;
  border-bottom: 1.5px solid #3e3efe;
}

.opportunity-name-inline-input::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

label {
  color: #344054;
  font-weight: 500;
  font-size: 12px;
  margin-bottom: 4px;
  display: block;
}

.required-field {
  color: #dc3545;
  font-size: 1.1rem;
  margin-left: 2px;
}

.form-fields-row {
  padding-left: 10px;
  padding-right: 10px;
  margin-bottom: 12px;
}

.form-group {
  margin-bottom: 0;
  min-height: 50px;
}

.date-selector-label {
  color: #344054;
  font-weight: 400;
  font-size: 12px;
  margin-bottom: 4px;
  display: block;
}

.date-selector-label .required-field {
  color: #dc3545;
  font-size: 1.1rem;
  margin-left: 2px;
}

.error-message {
  margin-top: 6px;
  display: block;
  font-size: 12px;
}

.action-buttons-row {
  padding-left: 10px;
  padding-right: 10px;
  margin-bottom: 8px;
}

.modal-footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.form {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px 20px;
}

.btn-new-field {
  background-color: transparent;
  font-size: 0.9rem;
  padding: 2px 4px;
  margin-bottom: 7px;
  border: none;
  color: #344054;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 400;
}

.btn-new-field:hover {
  color: #2d60ff;
}

.btn-new-field:hover .btn-new-field-text {
  color: #2d60ff;
}

.btn-new-field:hover .btn-new-field-icon {
  color: #2d60ff;
}

.btn-new-field-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
  color: #3e3efe;
  flex-shrink: 0;
}

.btn-new-field-text {
  font-size: 13px;
  font-weight: 400;
  color: #1f2937;
}

.product-card {
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  margin-left: 10px;
  margin-right: 10px;
  margin-top: 8px;
}

.product-card .product-list-card-body {
  max-height: 150px;
  overflow-x: hidden;
  overflow-y: auto;
}

.product-card .header {
  font-size: 13px;
  font-weight: 600;
  color: #475467;
}

.product-card .values {
  font-size: 11px;
  font-weight: 400;
  color: #344054;
}

.delete-icon {
  font-size: 16px;
  color: #dc3545;
  cursor: pointer;
  transition: color 0.2s ease;
}

.delete-icon:hover {
  color: #c82333;
}

.product-card .row {
  padding-top: 4px;
  padding-bottom: 4px;
}

.product-card .value-row {
  padding-top: 6px;
  padding-bottom: 6px;
}

.product-card .striped {
  background-color: #edf9ff;
}

.empty-products-state {
  padding: 30px 20px;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-products-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
}

.empty-products-image {
  opacity: 0.6;
}

.empty-products-text {
  font-size: 14px;
  font-weight: 400;
  color: #6b7280;
  margin: 0;
}

.btn-save {
  border-radius: 8px;
  background-color: #2d60ff;
  color: #ffffff;
  font-size: 12px;
  font-weight: 400;
  padding: 8px 16px;
  border: none;
  min-width: 120px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.btn-save:hover:not(:disabled) {
  background-color: #1e4dcc;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
}

.btn-save:active:not(:disabled) {
  transform: translateY(1px);
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #9ca3af;
}

.add-option-btn-side {
  background: transparent;
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  align-self: flex-end;
  margin-bottom: 10px;
}

.add-option-btn-side:hover {
  background: transparent;
}

.row-spacing {
  margin-top: 20px;
}

.lead-source-row {
  width: 100%;
  display: flex;
  gap: 6px;
  align-items: center;
}

.lead-source-select {
  flex: 1;
}

.add-option-plus-icon {
  font-size: 20px;
  color: #2d60ff;
}

/* Fix required asterisk size causing label row height mismatch between paired fields */
:deep(.ui-input__required),
:deep(.ui-select__required) {
  font-size: 12px;
  line-height: 1;
  vertical-align: middle;
}

/* Align form-group labels so paired Input/Select fields sit at same vertical position */
.form-group :deep(.ui-input__label),
.form-group :deep(.ui-select__label) {
  height: 18px;
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}
</style>
